import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Alert, Badge, Box, Button, Group, Stack, Text } from '@mantine/core';
import { POST_SESSION_PRIMARY_BG } from '../../../../components/session/postSessionCard';
import { ContentCard } from '../../../../components/common/ContentCard';
import { Edit3, Info, Star } from 'react-feather';
import { RichTextContent } from '../../../../components/common/RichTextEditor';
import SendRecapEmailModal from './SendRecapEmailModal';
import { injectKeyThemesIntoTemplate } from './recapEmailBody';
import {
  DEFAULT_RECAP_SUBJECT,
  resolveTemplateMergeTags,
} from './recapEmailTemplate';
import {
  coachPostSessionApi,
  type PostSessionParticipant,
} from '../../../../services/api/coachPostSession';
import type {
  ComposeEnvelope,
  RecapDraftOutput,
} from '../../../../services/api/aiTypes';

/**
 * MED-901 / E2.16 — Recap Email Draft card.
 *
 * Replaces the 2.6a inline-edit pattern with a read-only preview that opens
 * the new `SendRecapEmailModal` (AC1, AC2). The modal saves on close.
 *
 * MED-1213 (Case 2) — draft edits are DURABLE: persisted server-side via
 * `saveDraftFn` on modal close (no autosave), and re-loaded from the bundle's
 * `recap_draft_edit` on the next visit. Edits now survive a tab/browser close
 * or a different device — the earlier sessionStorage-only behavior is retired.
 */

// MED-1051 — Figma "AI-DRAFTED RECAP EMAIL" (node 2059:34215) tokens.
const BAND_DIVIDER = '#D1D5DB'; // header / footer band dividers
const FIELD_BORDER = '#E5E7EB'; // To / Subject / Body field-box border
const LABEL_COLOR = '#9CA3AF'; // uppercase field labels
const VALUE_COLOR = '#374151'; // field values + title

const fieldBoxStyle = {
  border: `1px solid ${FIELD_BORDER}`,
  borderRadius: 8,
  backgroundColor: '#FFFFFF',
  padding: '9px 13px',
} as const;

interface Props {
  sessionId: string;
  participants: PostSessionParticipant[];
  groupName: string | null;
  cohortName: string;
  sessionNumber: number | null;
  /** ISO 8601 scheduled_start — used for the `{{session_date}}` merge tag */
  sessionDateIso: string | null;
  topicName: string;
  coachName: string;
  /** MED-893 AC8 — when present and `result` is non-null, the AI-generated
   *  recap email subject + body seed the draft on first visit. A saved
   *  server-side draft edit (`savedDraftEdit`) takes precedence.
   *  fail_open / null result → no pre-pop. */
  recapDraftEnvelope: ComposeEnvelope<RecapDraftOutput> | null;
  /** MED-971 AC2 — ISO 8601 of the cohort's next scheduled session, used by the
   *  `{{next_session_date}}` merge tag in both the template seed and the modal
   *  preview. Null when no future session is scheduled. */
  nextSessionDateIso: string | null;
  /** MED-971 AC1 — admin-managed default body HTML (from `system_configs.recap_email_default_body`).
   *  Null when no template is configured — body stays empty in that case (AC7). */
  defaultTemplate: string | null;
  /** MED-971 AC4/AC5 — true iff the cohort's group_coaching modality has
   *  `ai_features_enabled=true`. Used to gate template seeding: when AI is on,
   *  the card waits for `recapDraftEnvelope` to resolve before seeding so the
   *  template can't beat the AI to the body field. */
  aiFeaturesEnabled: boolean;
  /** MED-1128 — true when modality AI is on but AI listening was OFF for THIS
   *  session (no transcript). The card renders the plain template (aiFeaturesEnabled
   *  is passed as the effective modality&&listening flag) plus an inline note
   *  explaining why no AI draft is available. */
  aiListeningOffForSession?: boolean;
  /** MED-909 — pass-through to `SendRecapEmailModal.sendFn`. Default behavior
   *  (when omitted) is to use the coach send endpoint. Peer leader caller
   *  passes `peerLeaderPostSessionApi.sendRecapEmail` here. */
  sendFn?: (
    sessionId: string,
    body: { recipient_user_ids: string[]; subject: string; body_html: string }
  ) => Promise<{
    sent: number;
    failed: number;
    skipped_not_in_session: number;
  }>;
  /** MED-1213 (Case 2) — the leader's durable draft edit from the bundle
   *  (`recap_draft_edit`). When it has content, it seeds the initial draft so
   *  the edited draft wins over the AI seed (AC2); null/empty → the AI/template
   *  seed runs, so a blank/cleared draft re-seeds from the AI draft (AC3). */
  savedDraftEdit: { subject: string | null; body_html: string | null } | null;
  /** MED-1213 — persist the draft edit server-side on modal close. Defaults to
   *  the coach endpoint (mirrors `sendFn`); the peer page passes the peer one. */
  saveDraftFn?: (
    sessionId: string,
    body: { subject: string; body_html: string }
  ) => Promise<unknown>;
}

interface DraftState {
  subject: string;
  bodyHtml: string;
}

export function RecapEmailDraftCard({
  sessionId,
  participants,
  groupName,
  cohortName,
  sessionNumber,
  sessionDateIso,
  topicName,
  coachName,
  recapDraftEnvelope,
  nextSessionDateIso,
  defaultTemplate,
  aiFeaturesEnabled,
  aiListeningOffForSession = false,
  sendFn,
  savedDraftEdit,
  saveDraftFn,
}: Props) {
  // MED-1213 — seed the draft from the durable server-side edit. Empty/null
  // fields fall through to '' so the one-shot AI/template seed useEffect runs
  // (its `if (subject || bodyHtml) return` guard treats '' as "not yet seeded").
  const [subject, setSubject] = useState<string>(savedDraftEdit?.subject ?? '');
  const [bodyHtml, setBodyHtml] = useState<string>(
    savedDraftEdit?.body_html ?? ''
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  // MED-971 AC1/AC5/AC7 — admin-managed template seed with three session-level
  // merge tags pre-resolved. Used as the fallback when no AI seed is available
  // (AI features off, or AI features on but draft failed), and — as of MED-972 —
  // as the structural wrapper for the AI seed too. Null when no template is
  // configured — body stays empty in that case (AC7).
  const templateInitial = useMemo(() => {
    if (!defaultTemplate) return null;
    return resolveTemplateMergeTags(defaultTemplate, {
      coachName,
      sessionNumber,
      nextSessionDateIso,
    });
  }, [defaultTemplate, coachName, sessionNumber, nextSessionDateIso]);

  // MED-893 AC8 / MED-972 — derive the canonical AI seed for this session. Mock
  // fixtures are deterministic per session_id, so on refresh `aiInitial`
  // reproduces what it was before — used both to seed the initial draft and to
  // drive the "AI-drafted · review before sending" pill (pill shows while the
  // visible content is byte-for-byte the seed).
  //
  // 2026-06-11 (coach review, post-MED-972): the AI's ONLY email contribution is
  // key_themes, slotted into the admin template's bullet region — the template's
  // prose stays authoritative. With NO template configured there is no AI seed
  // at all: the old full-AI-body fallback is retired ("if the template is
  // missing, just say that — don't come up with new recap email content"), and
  // the card shows a template-missing notice instead. The subject is no longer
  // the AI's either — the Send modal's E2.16 default applies.
  const aiInitial = useMemo(() => {
    if (!recapDraftEnvelope?.result || !templateInitial) return null;
    const recap = recapDraftEnvelope.result.recap_email;
    // MED-1151 — AI takeaway generation that returns NO key_themes (thin input:
    // the recipe stays empty rather than fabricate; see recipes/recapDraft.ts)
    // is treated as "no AI seed", exactly like a fail_open/null result. The
    // one-shot seed below then falls through to `templateInitial` — the non-AI
    // default, i.e. the same draft a coach gets with AI off (AC3). Returning a
    // non-null seed here would (a) leave the template's empty `<li>` bullets in
    // place yet (b) light up the "AI-drafted" badge, producing the reported
    // hollow draft that looks AI-written but has no takeaways. Empty themes →
    // null → no badge, plain template, never a blank/hollow "AI" draft.
    if (recap.key_themes.length === 0) return null;
    return {
      bodyHtml: injectKeyThemesIntoTemplate(templateInitial, recap.key_themes),
    };
  }, [recapDraftEnvelope, templateInitial]);

  // True when AI produced themes but there is no admin template to put them in —
  // the one state the coach must hear about rather than silently getting nothing.
  const templateMissingForAi =
    aiFeaturesEnabled &&
    recapDraftEnvelope?.result != null &&
    templateInitial === null;

  // MED-971: the template seed must NOT fire before we know whether AI is
  // available — otherwise the template lands in the body field first and the
  // one-shot `if (subject || bodyHtml) return` guard then blocks the AI seed
  // from replacing it (AC4 would silently regress to "template wins"). The
  // AI decision is final when:
  //   • AI features are off for the cohort (no AI fetch ever happens), or
  //   • AI features are on and the envelope has resolved (success or fail_open).
  const aiSeedReady = !aiFeaturesEnabled || recapDraftEnvelope !== null;

  // One-shot seed: only when the saved draft is empty. A coach's saved work
  // always wins. Priority: AI seed (AC4) > template seed (AC1/AC5) > empty (AC7).
  //
  // MED-1252 — seed the default subject alongside the body (both branches), so
  // a fresh draft shows the default subject on first render without an editor
  // round-trip (AC1). We store the RAW template (with `{{…}}` tokens) so the
  // Send modal keeps its editable merge tags (AC3); the read-only card resolves
  // the tags for display via `displaySubject` below (AC2). The seed only fires
  // when BOTH subject and body are empty (a genuinely fresh draft) — a
  // deliberately-cleared subject reloads with a non-empty saved body, so this
  // guard returns early and never re-seeds, preserving the empty state (AC5).
  useEffect(() => {
    if (subject || bodyHtml) return;
    if (!aiSeedReady) return;
    if (aiInitial) {
      setBodyHtml(aiInitial.bodyHtml);
      setSubject(DEFAULT_RECAP_SUBJECT);
      return;
    }
    if (templateInitial) {
      setBodyHtml(templateInitial);
      setSubject(DEFAULT_RECAP_SUBJECT);
    }
  }, [aiInitial, templateInitial, aiSeedReady, subject, bodyHtml]);

  // MED-1252 (AC2) — resolve the subject's session-level merge tags for the
  // read-only display, mirroring how the body's tags are resolved on seed.
  // Resolving at render time (rather than baking resolved text into state)
  // lets the stored `subject` keep its raw tokens for the editor (AC3).
  // An empty subject resolves to '' → the "No subject yet" placeholder (AC5).
  const displaySubject = useMemo(
    () =>
      resolveTemplateMergeTags(subject, {
        coachName,
        sessionNumber,
        nextSessionDateIso,
        cohortName,
      }),
    [subject, coachName, sessionNumber, nextSessionDateIso, cohortName]
  );

  // MED-1213 — save the draft edit server-side when the modal closes (no
  // autosave). Fire-and-forget: a failed save must not crash the card — the
  // in-tab preview still reflects the edit; only cross-session durability is
  // lost. The bundle re-loads the saved edit on the next visit.
  const handleDraftChange = useCallback(
    ({ subject: s, bodyHtml: b }: DraftState) => {
      setSubject(s);
      setBodyHtml(b);
      const save = saveDraftFn ?? coachPostSessionApi.saveRecapDraftEdit;
      void Promise.resolve(save(sessionId, { subject: s, body_html: b })).catch(
        () => {
          // Swallow — durability is best-effort; the UI keeps the edit in-tab.
        }
      );
    },
    [sessionId, saveDraftFn]
  );

  // Pill visible only while the BODY still matches the AI seed byte-for-byte.
  // Body only (2026-06-11): the subject is the E2.16 default, not an AI
  // contribution — the Send modal applying or the coach tweaking it must
  // neither show nor hide the pill. A body edit hides it.
  const aiDrafted =
    aiInitial !== null && bodyHtml === aiInitial.bodyHtml && bodyHtml !== '';

  const recipientCount = participants.length;
  const toLabel = groupName
    ? `${groupName} participants (${recipientCount})`
    : `Group participants (${recipientCount})`;

  const hasDraft = subject.trim().length > 0 || bodyHtml.trim().length > 0;

  return (
    <>
      <ContentCard
        variant="elevated"
        padding={0}
        contentGap={0}
        fill={false}
        header={null}
        data-testid="recap-email-draft-card"
        style={{ overflow: 'hidden' }}
      >
        {/* Header band */}
        <Group
          justify="space-between"
          align="center"
          wrap="nowrap"
          style={{
            padding: '16px 20px',
            borderBottom: `1px solid ${BAND_DIVIDER}`,
          }}
        >
          <Text fw={700} fz={14} c={VALUE_COLOR}>
            Recap Email Draft
          </Text>
          {/* AC4 — AI-drafted badge: shown only while the body still matches the
              AI seed; the header badge is the sole AI indicator (AC3). */}
          {aiDrafted && (
            <Badge
              size="sm"
              variant="light"
              leftSection={<Star size={10} />}
              styles={{
                root: {
                  backgroundColor: '#f3f0ff',
                  color: '#5a3eb6',
                  textTransform: 'none',
                  fontWeight: 500,
                },
              }}
            >
              AI-drafted &middot; review before sending
            </Badge>
          )}
        </Group>

        {/* Body region — To / Subject / Body as plain field boxes (AC3). */}
        <Stack gap={14} style={{ padding: 20 }}>
          {/* MED-1128 — AI was on for the modality but listening was off for this
              session, so there is no transcript to draft from. Same plain-template
              experience as modality-off, with a brief note explaining why. */}
          {aiListeningOffForSession && (
            <Alert
              color="gray"
              variant="light"
              icon={<Info size={14} />}
              title="No AI-drafted recap for this session"
            >
              <Text size="sm">
                AI listening was off for this session, so no AI-drafted recap is
                available. You can still compose and send a recap below.
              </Text>
            </Alert>
          )}
          {templateMissingForAi && (
            <Alert
              color="yellow"
              variant="light"
              icon={<Info size={14} />}
              title="Recap template missing"
            >
              <Text size="sm">
                No recap email template is configured, so the AI&apos;s key
                themes have nowhere to go. Ask a Medley admin to set the default
                recap template — until then, compose the email yourself below.
              </Text>
            </Alert>
          )}

          <Field label="To">
            <Box style={fieldBoxStyle}>
              <Text fz={13} c={VALUE_COLOR}>
                {toLabel}
              </Text>
            </Box>
          </Field>

          {hasDraft ? (
            <>
              <Field label="Subject">
                <Box style={fieldBoxStyle}>
                  <Text fz={13} c={VALUE_COLOR}>
                    {displaySubject.trim() || (
                      <Text component="span" c="dimmed" fs="italic">
                        No subject yet
                      </Text>
                    )}
                  </Text>
                </Box>
              </Field>
              <Field label="Body">
                <Box
                  style={{
                    ...fieldBoxStyle,
                    padding: '12px 14px',
                    minHeight: 200,
                  }}
                >
                  {bodyHtml.trim() ? (
                    <Box style={{ color: VALUE_COLOR, fontSize: 13 }}>
                      <RichTextContent content={bodyHtml} />
                    </Box>
                  ) : (
                    <Text size="sm" c="dimmed" fs="italic">
                      No body yet
                    </Text>
                  )}
                </Box>
              </Field>
            </>
          ) : (
            <Box
              style={{
                ...fieldBoxStyle,
                padding: 16,
                textAlign: 'center',
              }}
            >
              <Text size="sm" c="dimmed">
                No recap drafted yet. Click <strong>Edit &amp; Send</strong> to
                compose a recap email for your participants.
              </Text>
            </Box>
          )}
        </Stack>

        {/* Footer band — single Edit & Send (opens the existing modal). */}
        <Group
          justify="flex-end"
          style={{
            padding: '16px 20px',
            borderTop: `1px solid ${BAND_DIVIDER}`,
          }}
        >
          <Button
            size="sm"
            radius={8}
            leftSection={<Edit3 size={14} />}
            onClick={() => setModalOpen(true)}
            disabled={participants.length === 0}
            styles={{
              root: {
                backgroundColor: POST_SESSION_PRIMARY_BG,
                color: '#FFFFFF',
              },
            }}
          >
            Edit &amp; Send
          </Button>
        </Group>
      </ContentCard>

      <SendRecapEmailModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        onDraftChange={handleDraftChange}
        initialSubject={subject}
        initialBodyHtml={bodyHtml}
        aiDrafted={aiDrafted}
        sessionId={sessionId}
        participants={participants}
        cohortName={cohortName}
        groupName={groupName ?? ''}
        sessionNumber={sessionNumber}
        sessionDateIso={sessionDateIso}
        topicName={topicName}
        coachName={coachName}
        nextSessionDateIso={nextSessionDateIso}
        sendFn={sendFn}
      />
    </>
  );
}

/** MED-1051 — uppercase field label + its boxed value (To / Subject / Body). */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Text
        fz={11}
        fw={700}
        c={LABEL_COLOR}
        tt="uppercase"
        style={{ letterSpacing: '0.44px' }}
      >
        {label}
      </Text>
      {children}
    </Stack>
  );
}

export default RecapEmailDraftCard;
